import { collection, getDocs, query, where } from 'firebase/firestore';
import { useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import Sidebar from '../components/Sidebar';
import { auth, db } from '../firebase';

const STATUS_CONFIG = {
  staying:    { label: 'Staying',     bg: '#DCFCE7', color: '#166534', border: '#86EFAC' },
  scheduled:  { label: 'Scheduled',   bg: '#DBEAFE', color: '#1E40AF', border: '#93C5FD' },
  noresponse: { label: 'No Response', bg: '#FEE2E2', color: '#CC0000', border: '#FCA5A5' },
};

const PARTD_STATUS_CONFIG = {
  keeping:   { label: 'Keeping Plan', bg: '#DCFCE7', color: '#166534', border: '#86EFAC' },
  scheduled: { label: 'Scheduled',    bg: '#DBEAFE', color: '#1E40AF', border: '#93C5FD' },
  noresponse:{ label: 'No Response',  bg: '#FEE2E2', color: '#CC0000', border: '#FCA5A5' },
};

const styles = {
  screen: { minHeight: '100vh', backgroundColor: '#F4F6F9', fontFamily: 'system-ui, sans-serif' },
  main: { marginLeft: 220, padding: 24 },
  topbar: { display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 24 },
  pageTitle: { fontSize: 22, fontWeight: '700', color: '#0A2D5A' },
  pageSub: { fontSize: 13, color: '#888', marginTop: 2 },
  addBtn: { backgroundColor: '#E8620A', color: 'white', border: 'none', borderRadius: 8, padding: '8px 16px', fontSize: 13, fontWeight: '600', cursor: 'pointer' },
  statsBar: { display: 'flex', gap: 12, marginBottom: 24 },
  statCard: { backgroundColor: 'white', borderRadius: 10, padding: '14px 20px', border: '0.5px solid #e0e0e0', flex: 1, textAlign: 'center' },
  statNum: { fontSize: 28, fontWeight: '700', color: '#0A2D5A' },
  statLabel: { fontSize: 12, fontWeight: '600', color: '#888', marginTop: 2, textTransform: 'uppercase', letterSpacing: 0.5 },
  aepGrid: { display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 12, marginBottom: 24 },
  aepCard: { borderRadius: 10, padding: '16px 20px', border: '1px solid', cursor: 'pointer' },
  aepCardActive: { borderRadius: 10, padding: '16px 20px', border: '2px solid', cursor: 'pointer', boxShadow: '0 2px 8px rgba(0,0,0,0.12)' },
  aepNum: { fontSize: 32, fontWeight: '700', marginBottom: 4 },
  aepLabel: { fontSize: 13, fontWeight: '700' },
  aepSub: { fontSize: 11, marginTop: 3 },
  sectionLabel: { fontSize: 11, fontWeight: '700', color: '#888', letterSpacing: 2, textTransform: 'uppercase', marginBottom: 12 },
  filterBar: { display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 12 },
  clearFilter: { fontSize: 12, color: '#E8620A', cursor: 'pointer', fontWeight: '600', background: 'none', border: 'none', padding: 0 },
  clientsCard: { backgroundColor: 'white', borderRadius: 12, border: '0.5px solid #e0e0e0', overflow: 'hidden' },
  tableHeader: {
    display: 'grid',
    gridTemplateColumns: '32px 220px 60px 120px 160px 130px',
    alignItems: 'center',
    padding: '8px 16px',
    borderBottom: '1px solid #eee',
    gap: 12,
  },
  tableHeaderText: { fontSize: 12, fontWeight: '700', color: '#aaa', letterSpacing: 1, textTransform: 'uppercase' },
  tableHeaderClickable: { fontSize: 12, fontWeight: '700', color: '#1D6FA4', letterSpacing: 1, textTransform: 'uppercase', cursor: 'pointer', userSelect: 'none' },
  clientRow: {
    display: 'grid',
    gridTemplateColumns: '32px 220px 60px 120px 160px 130px',
    alignItems: 'center',
    padding: '10px 16px',
    borderBottom: '0.5px solid #f0f0f0',
    gap: 12,
    cursor: 'pointer',
    transition: 'background-color 0.15s',
  },
  avatar: { width: 32, height: 32, borderRadius: '50%', backgroundColor: '#E6F1F9', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 11, fontWeight: '600', color: '#1D6FA4', flexShrink: 0 },
  clientName: { fontSize: 15, fontWeight: '600', color: '#111', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' },
  planBadge: { fontSize: 13, padding: '2px 10px', borderRadius: 10, fontWeight: '600', whiteSpace: 'nowrap', display: 'inline-block' },
  statusBadge: { fontSize: 13, padding: '2px 10px', borderRadius: 10, fontWeight: '600', whiteSpace: 'nowrap', display: 'inline-block' },
  divider: { height: 1, backgroundColor: '#e0e0e0', marginBottom: 24, marginTop: 8 },
};

export default function Dashboard() {
  const navigate = useNavigate();
  const location = useLocation();
  const [clients, setClients] = useState([]);
  const [agent, setAgent] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeFilter, setActiveFilter] = useState(null);
  const [activePartDFilter, setActivePartDFilter] = useState(null);
  const [sortAsc, setSortAsc] = useState(true);
  const [hoveredRow, setHoveredRow] = useState(null);

  const agentId = auth.currentUser?.uid;

  useEffect(() => {
    const fetchData = async () => {
      if (!agentId) return;
      setLoading(true);
      const q = query(collection(db, 'clients'), where('agentId', '==', agentId));
      const snap = await getDocs(q);
      const data = snap.docs.map(doc => ({ id: doc.id, ...doc.data() }));
      setClients(data);
      const { doc: docFn, getDoc } = await import('firebase/firestore');
      const agentSnap = await getDoc(docFn(db, 'agents', agentId));
      if (agentSnap.exists()) setAgent(agentSnap.data());
      setLoading(false);
    };
    fetchData();
  }, [agentId, location]);

  const partDEnabled = agent?.partDReviewEnabled === true;

  const advantageClients = clients.filter(c => c.planType === 'Advantage');
  const supplementClients = clients.filter(c => c.planType === 'Supplement');
  const staying = clients.filter(c => c.aepStatus === 'staying').length;
  const scheduled = clients.filter(c => c.aepStatus === 'scheduled').length;
  const noResponse = advantageClients.filter(c => !c.aepStatus || c.aepStatus === 'noresponse').length;
  const partDKeeping = supplementClients.filter(c => c.partDStatus === 'keeping').length;
  const partDScheduled = supplementClients.filter(c => c.partDStatus === 'scheduled').length;
  const partDNoResponse = supplementClients.filter(c => !c.partDStatus).length;

  const handleBucketClick = (filter) => {
    setActiveFilter(activeFilter === filter ? null : filter);
    setActivePartDFilter(null);
  };

  const handlePartDBucketClick = (filter) => {
    setActivePartDFilter(activePartDFilter === filter ? null : filter);
    setActiveFilter(null);
  };

  const getFilteredClients = () => {
    let list = clients;
    if (activeFilter === 'staying') list = clients.filter(c => c.aepStatus === 'staying');
    else if (activeFilter === 'scheduled') list = clients.filter(c => c.aepStatus === 'scheduled');
    else if (activeFilter === 'noresponse') list = advantageClients.filter(c => !c.aepStatus || c.aepStatus === 'noresponse');
    else if (activePartDFilter === 'keeping') list = supplementClients.filter(c => c.partDStatus === 'keeping');
    else if (activePartDFilter === 'scheduled') list = supplementClients.filter(c => c.partDStatus === 'scheduled');
    else if (activePartDFilter === 'noresponse') list = supplementClients.filter(c => !c.partDStatus);
    return [...list].sort((a, b) => {
      const nameA = (a.name || '').toLowerCase();
      const nameB = (b.name || '').toLowerCase();
      return sortAsc ? nameA.localeCompare(nameB) : nameB.localeCompare(nameA);
    });
  };

  const getFilterLabel = () => {
    if (activeFilter === 'staying') return 'Staying — Advantage';
    if (activeFilter === 'scheduled') return 'Appointment Scheduled — Advantage';
    if (activeFilter === 'noresponse') return 'No Response — Advantage';
    if (activePartDFilter === 'keeping') return 'Keeping Plan — Part D';
    if (activePartDFilter === 'scheduled') return 'Scheduled — Part D';
    if (activePartDFilter === 'noresponse') return 'No Response — Part D';
    return null;
  };

  const getInitials = (name) => {
    if (!name) return '??';
    return name.split(' ').map(n => n[0]).join('').toUpperCase();
  };

  const getAEPStatusBadge = (client) => {
    const status = client.aepStatus;
    if (client.planType === 'Advantage' && (!status || status === 'noresponse')) {
      return (
        <span style={{
          ...styles.statusBadge,
          backgroundColor: STATUS_CONFIG.noresponse.bg,
          color: STATUS_CONFIG.noresponse.color,
          border: `1px solid ${STATUS_CONFIG.noresponse.border}`,
        }}>
          No Response
        </span>
      );
    }
    const config = STATUS_CONFIG[status];
    if (!config) return null;
    return (
      <span style={{
        ...styles.statusBadge,
        backgroundColor: config.bg,
        color: config.color,
        border: `1px solid ${config.border}`,
      }}>
        {config.label}
      </span>
    );
  };

  const getPartDStatusBadge = (client) => {
    if (client.planType !== 'Supplement') return null;
    const status = client.partDStatus;
    const config = PARTD_STATUS_CONFIG[status] || PARTD_STATUS_CONFIG.noresponse;
    return (
      <span style={{
        ...styles.statusBadge,
        backgroundColor: config.bg,
        color: config.color,
        border: `1px solid ${config.border}`,
      }}>
        {config.label}
      </span>
    );
  };

  const filteredClients = getFilteredClients();
  const showPartDStatus = activePartDFilter !== null;

  return (
    <div style={styles.screen}>
      <Sidebar />
      <div style={styles.main}>
        <div style={styles.topbar}>
          <div>
            <div style={styles.pageTitle}>Dashboard</div>
            <div style={styles.pageSub}>
              {clients.length === 0
                ? 'Add your first client to get started'
                : advantageClients.length > 0
                ? `${noResponse} Advantage clients need attention`
                : `${clients.length} client${clients.length !== 1 ? 's' : ''} in your book`}
            </div>
          </div>
          <button style={styles.addBtn} onClick={() => navigate('/add-client')}>
            + Add Client
          </button>
        </div>

        {/* Stats Bar */}
        {clients.length > 0 && (
          <div style={styles.statsBar}>
            <div style={styles.statCard}>
              <div style={styles.statNum}>{clients.length}</div>
              <div style={styles.statLabel}>Total Clients</div>
            </div>
            <div style={styles.statCard}>
              <div style={{ ...styles.statNum, color: '#993C1D' }}>{advantageClients.length}</div>
              <div style={styles.statLabel}>Advantage</div>
            </div>
            <div style={styles.statCard}>
              <div style={{ ...styles.statNum, color: '#185FA5' }}>{supplementClients.length}</div>
              <div style={styles.statLabel}>Supplement</div>
            </div>
          </div>
        )}

        {/* Advantage AEP Section */}
        {advantageClients.length > 0 && (
          <>
            <div style={styles.sectionLabel}>AEP Status — Advantage Clients — Click to Filter</div>
            <div style={styles.aepGrid}>
              <div
                onClick={() => handleBucketClick('staying')}
                style={{
                  ...(activeFilter === 'staying' ? styles.aepCardActive : styles.aepCard),
                  backgroundColor: '#F0FDF4',
                  borderColor: activeFilter === 'staying' ? '#166534' : '#86EFAC',
                }}>
                <div style={{ ...styles.aepNum, color: '#166534' }}>{staying}</div>
                <div style={{ ...styles.aepLabel, color: '#166534' }}>Staying</div>
                <div style={{ ...styles.aepSub, color: '#4ADE80' }}>Confirmed, no action needed</div>
              </div>
              <div
                onClick={() => handleBucketClick('scheduled')}
                style={{
                  ...(activeFilter === 'scheduled' ? styles.aepCardActive : styles.aepCard),
                  backgroundColor: '#EFF6FF',
                  borderColor: activeFilter === 'scheduled' ? '#1E40AF' : '#93C5FD',
                }}>
                <div style={{ ...styles.aepNum, color: '#1E40AF' }}>{scheduled}</div>
                <div style={{ ...styles.aepLabel, color: '#1E40AF' }}>Appointment Scheduled</div>
                <div style={{ ...styles.aepSub, color: '#60A5FA' }}>Review booked</div>
              </div>
              <div
                onClick={() => handleBucketClick('noresponse')}
                style={{
                  ...(activeFilter === 'noresponse' ? styles.aepCardActive : styles.aepCard),
                  backgroundColor: '#FFF5F5',
                  borderColor: activeFilter === 'noresponse' ? '#CC0000' : '#FCA5A5',
                }}>
                <div style={{ ...styles.aepNum, color: '#CC0000' }}>{noResponse}</div>
                <div style={{ ...styles.aepLabel, color: '#CC0000' }}>No Response</div>
                <div style={{ ...styles.aepSub, color: '#F87171' }}>Follow up needed</div>
              </div>
            </div>
          </>
        )}

        {/* Part D Review Section */}
        {partDEnabled && supplementClients.length > 0 && (
          <>
            <div style={styles.divider} />
            <div style={styles.sectionLabel}>Part D Prescription Drug Plan Review — Supplement Clients — Click to Filter</div>
            <div style={styles.aepGrid}>
              <div
                onClick={() => handlePartDBucketClick('keeping')}
                style={{
                  ...(activePartDFilter === 'keeping' ? styles.aepCardActive : styles.aepCard),
                  backgroundColor: '#F0FDF4',
                  borderColor: activePartDFilter === 'keeping' ? '#166534' : '#86EFAC',
                }}>
                <div style={{ ...styles.aepNum, color: '#166534' }}>{partDKeeping}</div>
                <div style={{ ...styles.aepLabel, color: '#166534' }}>Keeping Plan</div>
                <div style={{ ...styles.aepSub, color: '#4ADE80' }}>Confirmed, no action needed</div>
              </div>
              <div
                onClick={() => handlePartDBucketClick('scheduled')}
                style={{
                  ...(activePartDFilter === 'scheduled' ? styles.aepCardActive : styles.aepCard),
                  backgroundColor: '#EFF6FF',
                  borderColor: activePartDFilter === 'scheduled' ? '#1E40AF' : '#93C5FD',
                }}>
                <div style={{ ...styles.aepNum, color: '#1E40AF' }}>{partDScheduled}</div>
                <div style={{ ...styles.aepLabel, color: '#1E40AF' }}>Appointment Scheduled</div>
                <div style={{ ...styles.aepSub, color: '#60A5FA' }}>Review booked</div>
              </div>
              <div
                onClick={() => handlePartDBucketClick('noresponse')}
                style={{
                  ...(activePartDFilter === 'noresponse' ? styles.aepCardActive : styles.aepCard),
                  backgroundColor: '#FFF5F5',
                  borderColor: activePartDFilter === 'noresponse' ? '#CC0000' : '#FCA5A5',
                }}>
                <div style={{ ...styles.aepNum, color: '#CC0000' }}>{partDNoResponse}</div>
                <div style={{ ...styles.aepLabel, color: '#CC0000' }}>No Response</div>
                <div style={{ ...styles.aepSub, color: '#F87171' }}>Follow up needed</div>
              </div>
            </div>
          </>
        )}

        <div style={styles.filterBar}>
          <div style={styles.sectionLabel}>
            {activeFilter || activePartDFilter ? `Showing: ${getFilterLabel()}` : 'All Clients'}
          </div>
          {(activeFilter || activePartDFilter) && (
            <button style={styles.clearFilter} onClick={() => { setActiveFilter(null); setActivePartDFilter(null); }}>
              Clear Filter
            </button>
          )}
        </div>

        <div style={styles.clientsCard}>
          {clients.length === 0 && !loading ? (
            <div style={{ padding: 48, textAlign: 'center' }}>
              <div style={{ fontSize: 18, fontWeight: '700', color: '#0A2D5A', marginBottom: 8 }}>
                Welcome to Medicare Plan Guardian
              </div>
              <div style={{ fontSize: 14, color: '#888', marginBottom: 24, lineHeight: '22px' }}>
                Add your first client to get started. After signing a client,<br />
                add their information here and send them their app link.
              </div>
              <button
                onClick={() => navigate('/add-client')}
                style={{ backgroundColor: '#E8620A', color: 'white', border: 'none', borderRadius: 8, padding: '10px 24px', fontSize: 14, fontWeight: '600', cursor: 'pointer' }}>
                + Add Your First Client
              </button>
            </div>
          ) : (
            <>
              <div style={styles.tableHeader}>
                <div />
                <div
                  style={styles.tableHeaderClickable}
                  onClick={() => setSortAsc(a => !a)}>
                  Name {sortAsc ? '↑' : '↓'}
                </div>
                <div style={styles.tableHeaderText}>State</div>
                <div style={styles.tableHeaderText}>Plan Type</div>
                <div style={styles.tableHeaderText}>Carrier</div>
                <div style={styles.tableHeaderText}>
                  {showPartDStatus ? 'Part D Status' : 'AEP Status'}
                </div>
              </div>
              {filteredClients.length === 0 && !loading && (
                <div style={{ padding: 24, textAlign: 'center', color: '#888', fontSize: 14 }}>
                  No clients in this category.
                </div>
              )}
              {filteredClients.map(client => (
                <div
                  key={client.id}
                  style={{
                    ...styles.clientRow,
                    backgroundColor: hoveredRow === client.id ? '#F0F6FF' : 'white',
                  }}
                  onMouseEnter={() => setHoveredRow(client.id)}
                  onMouseLeave={() => setHoveredRow(null)}
                  onClick={() => navigate(`/edit-client/${client.id}`)}>
                  <div style={styles.avatar}>{getInitials(client.name)}</div>
                  <div style={styles.clientName}>{client.name}</div>
                  <div style={{ fontSize: 14, color: '#888' }}>{client.state}</div>
                  <span style={{
                    ...styles.planBadge,
                    backgroundColor: client.planType === 'Advantage' ? '#FDF0E8' : '#E6F1F9',
                    color: client.planType === 'Advantage' ? '#993C1D' : '#185FA5',
                  }}>
                    {client.planType}
                  </span>
                  <div style={{ fontSize: 14, color: '#555' }}>{client.carrier}</div>
                  <div>
                    {showPartDStatus
                      ? getPartDStatusBadge(client)
                      : getAEPStatusBadge(client)
                    }
                  </div>
                </div>
              ))}
            </>
          )}
        </div>
      </div>
    </div>
  );
}